import { Component, OnInit } from '@angular/core';
import { CompanyLogo } from '../../models/company-logos.model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder } from '@angular/forms';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CompanyLogosService } from '../../services/company-logos.service';
import { WorkLocationsService } from '@app/modules/work-locations/services/work-locations.service';
import { finalize } from 'rxjs';
import moment from 'moment';
import { routes } from '@app/consts';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { base64ToFile } from 'ngx-image-cropper';

@Component({
  selector: 'app-edit-company-logo-file',
  templateUrl: './edit-company-logo-file.component.html',
  styleUrls: ['./edit-company-logo-file.component.scss']
})
export class EditCompanyLogoFileComponent implements OnInit {
  formId: string ='frm_BiyrhWjHEwhBo3';
  getFormData: boolean = false;
  formValid: boolean = false;
  formData: any = {
    file: null,
  };

  active: boolean = false;
  isLoading: boolean;
  companyLogoId: string;
  companyLogo: CompanyLogo;

  public columns: any[] = [
    {field: 'name', title: 'Name', dataType: "String"},
    { field: "streetAddress", title: "Street Address", tableId: "tfi_WlStreetAddress", dataType: "String"},
    { field: "city", title: "City", dataType: "String", tableId: "tfi_WlCity" },
    { field: "province", title: "Province", dataType: "String", tableId: "tfi_WlProvince" },
    { field: "zip", title: "Zip", dataType: "String", tableId: "tfi_WlZip" },
    { field: "region", subField: 'text', title: "Region", dataType: "String", tableId: "" },
    { field: "country", subField: 'name', title: "Country", dataType: "String", tableId: "tfi_WlCountry" },
    { field: "timezone", subField: 'text', title: "Timezone", dataType: "String", tableId: "" },
    { field: "companyLogos", subField: 'name', title: "stylingColor", tableId: "" },
    { field: "startDate", title: "Start Date", type: "date", dataType: "Date", tableId: "" },
    { field: "endDate", title: "End Date", type: "date", dataType: "Date", tableId: "" },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name"},
    { field: "streetAddress", sortValue: "streetAddress" },
    { field: "city", sortValue: "city" },
    { field: "province", sortValue: "province" },
    { field: "zip", sortValue: "zip" },
    { field: "region", sortValue: "region.text" },
    { field: "country", sortValue: "country.name" },
    { field: "timezone", sortValue: "timezone.text" },
    { field: "startDate", sortValue: "startDate" },
    { field: "endDate", sortValue: "endDate" },
    { field: "stylingColor", sortValue: "stylingColor" },
  ];

  public filterCategories: any[] = [].concat(this.columns)
  public searchFilterString: string;
  public searchValue: string;
  filterString: string;
  sortString: string;
  getWorkLocationsRequest: any;
  pageSize: number = 20;
  skip: number = 0;
  filterToggleDetails: {
    text: string,
    checked: boolean
  } = {
    text: 'FilterInactive',
    checked: true
  }
  public gridDataResult: GridDataResult;
  isLoadingWorkLocations: boolean;
  companyLogoImage: string;
  isLoadingImage: boolean;
  logoImage$: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
    private companyLogosService: CompanyLogosService,
    private workLocationsService: WorkLocationsService,
  ) { }

  ngOnInit(): void {
    this.companyLogoId = this.getIdInURL();
    if(this.companyLogoId){
      this.logoImage$ = this.companyLogosService.getCompanyLogoImage(this.companyLogoId)

      this.getWorkLocations();
    }
  }

  getIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
        params => IdInURL = params.get('id')
    );

    return IdInURL;
  }
  
  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData) {
    this.overlayService.show()

    this.companyLogosService.putCompanyLogoImage(this.getIdInURL(), formData)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  close(){
    this.router.navigate([`${routes.SITE_SETTINGS}${routes.STYLING}${routes.COMPANY_LOGOS}`]);
  }

  getWorkLocations(){
    this.isLoadingWorkLocations = true;

    let filter: string = `(companyLogo.Id = "${this.companyLogoId}")`;
    
    if(this.searchFilterString) {
      filter = `${filter} AND ${this.searchFilterString}`;
    }

    if(this.filterString) {
      if(filter){
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.getWorkLocationsRequest = this.workLocationsService.getWorkLocations(this.pageSize, this.skip, filter, this.sortString, this.filterToggleDetails.checked)
    .pipe(
        finalize(()=>{ this.isLoadingWorkLocations = false; })
    )
    .subscribe(
      res => {
        this.gridDataResult = {
          data: res.data,
          total: res.total,
        }
      }
    )
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getWorkLocations();
  }

  filterCallback(filterString: string) {
    this.filterString = filterString;
    this.skip = 0;;
    this.getWorkLocations();
  }

  search(searchValue) {
    this.searchValue = searchValue;
    this.searchFilterString = `(Name like "${this.searchValue}")`;
    this.getWorkLocationsRequest?.unsubscribe();
    this.getWorkLocations();
  }

  updateFilterToggleDetails(toggleChange: MatSlideToggleChange){
    this.filterToggleDetails.checked = toggleChange.checked;
    this.getWorkLocations();
  }

  sortChange(sortString: string){
    this.sortString = sortString;
    this.getWorkLocations();
  }


}
